/**
 * SaveArticleButton — toggle "Read Later" for an article.
 *
 * Renders three visual states:
 *   - Signed-out: shows the bookmark icon; a click opens the login modal.
 *   - Signed-in + unsaved: outlined bookmark; a click saves + shows toast.
 *   - Signed-in + saved: filled gold bookmark; a click removes.
 *
 * Optimistic UI — the button flips instantly and rolls back on API error.
 * Deliberately does NOT stop event propagation by default; when wrapped
 * inside an article card `<a>` link, callers should pass
 * `stopPropagation` so clicks don't also trigger navigation.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bookmark } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

export default function SaveArticleButton({
  articleId,
  initialSaved = false,
  size = "md",
  variant = "icon",     // "icon" | "chip"
  className = "",
  stopPropagation = true,
  onChange,
}) {
  const { user, login } = useAuth();
  const [saved, setSaved] = useState(initialSaved);
  const [pending, setPending] = useState(false);

  // Sync when the parent hands us a fresh initialSaved (e.g. after login
  // status batch-fetch resolves).
  useEffect(() => {
    setSaved(initialSaved);
  }, [initialSaved]);

  const toggle = useCallback(async (e) => {
    if (stopPropagation && e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!user) {
      toast.info("Sign in to save articles for later");
      login?.();
      return;
    }
    if (pending) return;
    const nextSaved = !saved;
    // Optimistic
    setSaved(nextSaved);
    setPending(true);
    try {
      if (nextSaved) {
        await axios.post(`${API}/articles/${articleId}/save`, {}, { withCredentials: true });
        toast.success("Saved to Read Later");
      } else {
        await axios.delete(`${API}/articles/${articleId}/save`, { withCredentials: true });
        toast("Removed from Read Later");
      }
      onChange?.(nextSaved);
    } catch (err) {
      setSaved(!nextSaved); // rollback
      toast.error(err?.response?.data?.detail || "Couldn't update Read Later");
    } finally {
      setPending(false);
    }
  }, [articleId, saved, user, login, pending, stopPropagation, onChange]);

  const sizes = { sm: "w-3.5 h-3.5", md: "w-4 h-4", lg: "w-5 h-5" };
  const icon = <Bookmark
    className={`${sizes[size] || sizes.md} ${saved ? "fill-tbc-gold text-tbc-gold" : ""}`}
    strokeWidth={saved ? 0 : 2}
  />;

  if (variant === "chip") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={pending}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
          saved
            ? "bg-tbc-gold/10 border-tbc-gold text-tbc-navy"
            : "bg-white/90 border-tbc-navy/15 text-tbc-navy/80 hover:border-tbc-navy"
        } ${className}`}
        aria-pressed={saved}
        aria-label={saved ? "Remove from Read Later" : "Save for later"}
        data-testid={`save-article-${articleId}`}
      >
        {icon}
        {saved ? "Saved" : "Save"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      className={`inline-flex items-center justify-center rounded-full w-8 h-8 transition-colors ${
        saved
          ? "bg-tbc-gold/15 text-tbc-gold hover:bg-tbc-gold/25"
          : "bg-white/90 text-tbc-navy/70 hover:text-tbc-navy hover:bg-white"
      } ${className}`}
      aria-pressed={saved}
      aria-label={saved ? "Remove from Read Later" : "Save for later"}
      data-testid={`save-article-${articleId}`}
    >
      {icon}
    </button>
  );
}
